import { ContentApi } from '@/api/modules/content';
import axios from 'axios';
import { createProgressCallback } from '@/utils/uploadHelpers';

/**
 * Supported file types for the upload system
 */
export const SUPPORTED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'video/mp4',
  'video/quicktime',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

/**
 * Document mime types
 */
export const DOCUMENT_MIME_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

/**
 * Detect content type based on file mime type, filename, backend content object, or upload progress object
 * @param {File|Object} fileOrContent - The file object, backend content object, or upload progress object
 * @returns {string} - 'image', 'video', or 'document'
 */
export function getContentType(fileOrContent) {
  if (!fileOrContent || typeof fileOrContent !== 'object') {
    return 'image';
  }

  // Check for upload progress objects first (they have contentType field in camelCase)
  if (fileOrContent.contentType) {
    return fileOrContent.contentType;
  }

  // Detect if this is a backend content object (has backend-specific fields)
  // vs a File object (has different structure)
  const isBackendObject = !!(fileOrContent.id || fileOrContent.filename || fileOrContent.content_type || fileOrContent.created_at);

  if (isBackendObject) {
    // Handle backend content objects - prioritize content_type over legacy type
    if (fileOrContent.content_type) {
      return fileOrContent.content_type;
    }

    // Legacy type field fallback (but only for non-document file types)
    if (fileOrContent.type && fileOrContent.type !== 'image') {
      return fileOrContent.type;
    }

    // Detect from filename if available (important for documents that might have type:'image')
    const fileName = fileOrContent.filename?.toLowerCase() || fileOrContent.name?.toLowerCase() || '';
    if (fileName.endsWith('.pdf') || fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
      return 'document';
    }
    if (fileName.endsWith('.mp4') || fileName.endsWith('.mov') || fileName.endsWith('.mpeg')) {
      return 'video';
    }

    // Default to image for backend objects
    return 'image';
  } else {
    // Handle File objects (for uploads) - type here is MIME type
    const mimeType = fileOrContent.type || '';
    const fileName = fileOrContent.name?.toLowerCase() || '';

    // Check document types first
    if (DOCUMENT_MIME_TYPES.includes(mimeType) || fileName.endsWith('.pdf') || fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
      return 'document';
    }

    // Check video types
    if (mimeType.startsWith('video/') || fileName.endsWith('.mp4') || fileName.endsWith('.mov') || fileName.endsWith('.mpeg')) {
      return 'video';
    }

    // Default to image
    return 'image';
  }
}

/**
 * Get document type from filename or backend content object
 * @param {string|Object} filenameOrContent - The filename string or backend content object
 * @returns {string|null} - 'pdf', 'doc', or null
 */
export function getDocumentType(filenameOrContent) {
  let filename;

  // Handle backend content objects
  if (typeof filenameOrContent === 'object' && filenameOrContent !== null) {
    // Check for document_type in extra_attributes (preferred for backend objects)
    if (filenameOrContent.extra_attributes?.document_type) {
      return filenameOrContent.extra_attributes.document_type;
    }

    // Fallback to filename detection
    filename = filenameOrContent.filename || filenameOrContent.name;
  } else {
    // Handle string filenames
    filename = filenameOrContent;
  }

  if (!filename) return null;
  const extension = filename.split('.').pop()?.toLowerCase();
  if (['pdf'].includes(extension)) return 'pdf';
  if (['doc', 'docx'].includes(extension)) return 'doc';
  return null;
}

/**
 * Get document processing status from backend content object
 * @param {Object} content - Backend content object
 * @returns {string|null} - Processing status or null
 */
export function getDocumentProcessingStatus(content) {
  if (!content || typeof content !== 'object') return null;
  // Use the general status field since extra_attributes is not available in the API response
  // For documents, the status field contains the processing status
  return content.status || null;
}

/**
 * Load PDF.js library from static files
 * @returns {Promise<Object>} - PDF.js library object
 */
async function loadPdfJs() {
  return new Promise((resolve, reject) => {
    // Check if PDF.js is already loaded
    if (window.pdfjsDistBuildPdf) {
      // Ensure worker is configured if not already done
      try {
        if (window.pdfjsDistBuildPdf.GlobalWorkerOptions && !window.pdfjsDistBuildPdf.GlobalWorkerOptions.workerSrc) {
          window.pdfjsDistBuildPdf.GlobalWorkerOptions.workerSrc = '/pdfjs/build/pdf.worker.js';
        } else if (window.pdfjsDistBuildPdf.workerSrc === undefined && window.pdfjsDistBuildPdf.disableWorker === undefined) {
          window.pdfjsDistBuildPdf.disableWorker = true;
        }
      } catch (error) {
        // Ignore configuration errors on already loaded instance
      }
      resolve(window.pdfjsDistBuildPdf);
      return;
    }

    // Create script element to load PDF.js from static files
    const script = document.createElement('script');
    script.src = '/pdfjs/build/pdf.js';
    script.onload = () => {
      if (window.pdfjsDistBuildPdf) {
        // Configure worker for older PDF.js versions
        try {
          if (window.pdfjsDistBuildPdf.GlobalWorkerOptions) {
            window.pdfjsDistBuildPdf.GlobalWorkerOptions.workerSrc = '/pdfjs/build/pdf.worker.js';
          } else if (window.pdfjsDistBuildPdf.workerSrc !== undefined) {
            window.pdfjsDistBuildPdf.workerSrc = '/pdfjs/build/pdf.worker.js';
          } else {
            // For very old versions, disable worker entirely
            window.pdfjsDistBuildPdf.disableWorker = true;
          }
        } catch (error) {
          console.warn('Could not configure PDF.js worker, using fallback:', error);
          // Fallback: try to disable worker for compatibility
          try {
            window.pdfjsDistBuildPdf.disableWorker = true;
          } catch (fallbackError) {
            // Ignore fallback errors
          }
        }
        resolve(window.pdfjsDistBuildPdf);
      } else {
        reject(new Error('PDF.js failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load PDF.js script'));
    document.head.appendChild(script);
  });
}

/**
 * Generate PDF thumbnail using static PDF.js files
 * @param {File} file - The PDF file
 * @param {Object} options - Options for thumbnail generation
 * @returns {Promise<string|null>} - Object URL of the thumbnail or null
 */
export async function generatePdfThumbnail(file, options = {}) {
  try {
    // Load PDF.js from static files
    const pdfjsLib = await loadPdfJs();

    // Create array buffer from file
    const arrayBuffer = await file.arrayBuffer();

    // Load PDF document
    const loadingTask = pdfjsLib.getDocument(arrayBuffer);
    const pdf = await loadingTask.promise;

    // Get first page
    const page = await pdf.getPage(1);

    // Set scale for thumbnail
    const scale = options.scale || 0.8;
    const viewport = page.getViewport(scale);

    // Create canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    // Render page to canvas
    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise;

    // Convert canvas to blob URL
    return new Promise(resolve => {
      canvas.toBlob(
        blob => {
          if (blob) {
            resolve(URL.createObjectURL(blob));
          } else {
            resolve(null);
          }
        },
        'image/jpeg',
        options.quality || 0.8
      );
    });
  } catch (error) {
    console.warn('Failed to generate PDF thumbnail:', error);
    return null;
  }
}

/**
 * Global registry to track active upload operations for cancellation
 */
const activeUploads = new Map();

/**
 * Registry to track video URLs that have been auto-uploaded to prevent duplicates
 * Key: video URL, Value: { uploadId, timestamp, status }
 */
const autoUploadRegistry = new Map();

/**
 * Cleanup old auto-upload registry entries (older than 1 hour)
 */
const cleanupAutoUploadRegistry = () => {
  const oneHourAgo = Date.now() - 60 * 60 * 1000; // 1 hour in milliseconds

  for (const [videoUrl, entry] of autoUploadRegistry.entries()) {
    if (entry.timestamp < oneHourAgo) {
      autoUploadRegistry.delete(videoUrl);
    }
  }
};

/**
 * Check if a video URL has already been uploaded or is currently being uploaded
 * @param {string} videoUrl - The video URL to check
 * @returns {boolean} - True if upload is in progress or already completed
 */
const isVideoAlreadyUploaded = videoUrl => {
  // Clean up old entries first
  cleanupAutoUploadRegistry();

  const entry = autoUploadRegistry.get(videoUrl);
  if (!entry) {
    return false; // Not uploaded
  }

  // Check if upload is in progress or completed
  return entry.status === 'uploading' || entry.status === 'completed';
};

/**
 * Register a video URL as being uploaded
 * @param {string} videoUrl - The video URL being uploaded
 * @param {string} uploadId - Unique upload identifier
 * @returns {boolean} - True if registration was successful, false if already registered
 */
const registerVideoUpload = (videoUrl, uploadId) => {
  // Check if already registered
  if (isVideoAlreadyUploaded(videoUrl)) {
    return false; // Already registered
  }

  // Register new upload
  autoUploadRegistry.set(videoUrl, {
    uploadId,
    timestamp: Date.now(),
    status: 'uploading'
  });

  return true; // Successfully registered
};

/**
 * Mark a video URL upload as completed
 * @param {string} videoUrl - The video URL that was uploaded
 * @param {Object} result - Upload result object
 */
const markVideoUploadCompleted = (videoUrl, result) => {
  const entry = autoUploadRegistry.get(videoUrl);
  if (entry) {
    entry.status = 'completed';
    entry.result = result;
    entry.timestamp = Date.now(); // Update timestamp
  }
};

/**
 * Mark a video URL upload as failed and remove from registry
 * @param {string} videoUrl - The video URL that failed to upload
 */
const markVideoUploadFailed = videoUrl => {
  autoUploadRegistry.delete(videoUrl); // Remove failed uploads to allow retry
};

/**
 * Auto-upload a generated video with deduplication
 * This function ensures each video URL is only uploaded once
 * @param {string} videoUrl - The video URL to upload
 * @param {Object} metadata - Video metadata (cameraEffect, fileName, etc.)
 * @param {Object} store - Vuex store instance for progress tracking
 * @returns {Promise<Object|null>} - Upload result or null if already uploaded/failed
 */
const autoUploadGeneratedVideo = async (videoUrl, metadata, store) => {
  if (!videoUrl || !metadata || !store) {
    console.error('Missing required parameters for autoUploadGeneratedVideo');
    return null;
  }

  // Generate unique upload ID
  const uploadId = `auto_upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Try to register this upload (prevents duplicates)
  if (!registerVideoUpload(videoUrl, uploadId)) {
    console.log('Video already being uploaded or completed, skipping duplicate upload:', videoUrl);
    return null; // Already uploaded or in progress
  }

  let globalUpload = null;

  try {
    console.log('Starting automatic upload for generated video:', videoUrl);

    // Fetch the video file from the URL
    const response = await axios.get(videoUrl, {
      responseType: 'blob'
    });

    // Create a File object from the blob
    const videoBlob = response.data;
    const fileName = metadata.fileName || `ai-generated-video-${metadata.cameraEffect.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.mp4`;
    const videoFile = new File([videoBlob], fileName, { type: 'video/mp4' });

    // Add upload to GlobalUploadProgress tracking
    const globalUploads = await store.dispatch('uploadProgress/addUploads', [videoFile]);
    globalUpload = globalUploads[0];

    // Show GlobalUploadProgress dialog
    store.dispatch('uploadProgress/showDialog');

    // Update progress for upload preparation phase
    await store.dispatch('uploadProgress/updateUpload', {
      id: globalUpload.id,
      data: {
        status: 'uploading'
      }
    });

    // Upload the video using the existing fileUpload utility with progress callback
    const uploadedContent = await fileUpload.uploadFile(videoFile, createProgressCallback(store, globalUpload.id), globalUpload.id);

    if (!uploadedContent || !uploadedContent.id) {
      throw new Error('Invalid response from upload');
    }

    // Update global progress with real content ID
    await store.dispatch('uploadProgress/updateUploadWithRealId', {
      tempId: globalUpload.id,
      realId: uploadedContent.id,
      data: {
        canDelete: true,
        contentType: 'video'
      }
    });

    // Mark upload as completed in registry
    markVideoUploadCompleted(videoUrl, uploadedContent);

    console.log('Video auto-uploaded successfully:', uploadedContent);

    // Dispatch global event for gallery refresh
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(
        new CustomEvent('content-uploaded', {
          detail: { contentId: uploadedContent.id, contentType: 'video' }
        })
      );
    }

    return uploadedContent;
  } catch (error) {
    console.error('Error during automatic video upload:', error);

    // Mark upload as failed (removes from registry to allow retry)
    markVideoUploadFailed(videoUrl);

    // Update global progress with error state if we have a reference
    if (globalUpload) {
      await store.dispatch('uploadProgress/updateUpload', {
        id: globalUpload.id,
        data: {
          progress: 100,
          status: 'error',
          message: error.message || 'Failed to upload AI generated video'
        }
      });
    }

    throw error; // Re-throw to let caller handle if needed
  }
};

/**
 * File upload utility for Flashy
 * Handles the entire process from getting presigned URLs to adding content to database
 * Now supports real cancellation of ongoing requests
 */
const fileUpload = {
  /**
   * Upload a single file and add it to content with cancellation support
   * @param {File} file - The file to upload
   * @param {Function} progressCallback - Optional callback to track upload progress
   * @param {string} uploadId - Unique identifier for this upload (for cancellation)
   * @returns {Promise<Object>} - The newly added content object
   */
  uploadFile: async (file, progressCallback = null, uploadId = null) => {
    if (!file) {
      throw new Error('No file provided');
    }

    // Create abort controller for this upload
    const abortController = new AbortController();
    const uploadOperations = {
      abortController,
      xhrRequest: null, // Will store the XMLHttpRequest for cancellation
      isCancelled: false
    };

    // Register this upload for potential cancellation
    if (uploadId) {
      activeUploads.set(uploadId, uploadOperations);
    }

    try {
      // Step 1: Prepare file data (0-10%)
      if (progressCallback) progressCallback(10);
      const fileData = await fileUpload.prepareFileData(file);

      // Check for cancellation
      if (uploadOperations.isCancelled) {
        throw new Error('Upload cancelled by user');
      }

      // Step 2: Get presigned URLs (10-33%) - Critical phase completion
      if (progressCallback) progressCallback(20);
      const response = await fileUpload.getPresignedUrl(fileData);

      // Check for cancellation
      if (uploadOperations.isCancelled) {
        throw new Error('Upload cancelled by user');
      }

      // 33% milestone reached - presign completed, uploads can continue seamlessly if user navigates
      if (progressCallback) progressCallback(33);

      // Step 3: Upload to storage (33-66%)
      if (progressCallback) progressCallback(50);
      await fileUpload.uploadToStorage(response.data.presignedUrls[0].presignedUrl, fileData, uploadOperations);

      // Check for cancellation
      if (uploadOperations.isCancelled) {
        throw new Error('Upload cancelled by user');
      }

      // 66% milestone reached - file is in storage
      if (progressCallback) progressCallback(66);

      // Step 4: Create database entry (66-100%)
      if (progressCallback) progressCallback(80);
      const content = await fileUpload.createContentEntry(response.data.contentMetadata);

      // Check for cancellation
      if (uploadOperations.isCancelled) {
        throw new Error('Upload cancelled by user');
      }

      // 100% - Upload completely finished
      if (progressCallback) progressCallback(100);

      return content;
    } catch (error) {
      // Ensure error state is communicated via progress callback
      if (progressCallback) progressCallback(-1); // Error state
      throw error;
    } finally {
      // Clean up the upload registration
      if (uploadId) {
        activeUploads.delete(uploadId);
      }
    }
  },

  /**
   * Cancel a specific upload by ID
   * @param {string} uploadId - The upload ID to cancel
   * @returns {boolean} - True if upload was found and cancelled
   */
  cancelUpload: uploadId => {
    const uploadOperations = activeUploads.get(uploadId);
    if (!uploadOperations) {
      return false;
    }

    uploadOperations.isCancelled = true;

    // Abort any ongoing HTTP requests
    uploadOperations.abortController.abort();

    // Abort XMLHttpRequest if it exists
    if (uploadOperations.xhrRequest) {
      uploadOperations.xhrRequest.abort();
    }

    // Clean up
    activeUploads.delete(uploadId);
    return true;
  },

  /**
   * Cancel all active uploads
   * @returns {number} - Number of uploads that were cancelled
   */
  cancelAllUploads: () => {
    const cancelledCount = activeUploads.size;

    // Cancel each active upload
    for (const [uploadOperations] of activeUploads) {
      uploadOperations.isCancelled = true;
      uploadOperations.abortController.abort();

      if (uploadOperations.xhrRequest) {
        uploadOperations.xhrRequest.abort();
      }
    }

    // Clear all active uploads
    activeUploads.clear();

    return cancelledCount;
  },

  /**
   * Get presigned URL from API with abort signal
   * @param {Object} fileData - Prepared file data
   * @returns {Promise<Object>} - API response with presigned URLs
   */
  getPresignedUrl: async fileData => {
    const shootId = 0; // Non-shoot-related content

    // Prepare file metadata, only include duration for videos
    const fileMetadata = {
      name: fileData.name,
      relativePath: fileData.relativePath,
      contentType: fileData.contentType,
      aspectRatio: fileData.aspectRatio
    };

    // Only include duration for video files
    if (fileData.contentType?.startsWith('video/') || fileData.isVideo) {
      fileMetadata.duration = fileData.duration;
    }

    // Note: We need to modify ContentApi to support abort signals
    // For now, this will work but won't be cancellable until API module is updated
    return await ContentApi.getPresignedUrls({
      id: shootId,
      files: [fileMetadata]
    });
  },

  /**
   * Upload file to storage using presigned URL with cancellation support
   * @param {string} presignedUrl - The URL to upload to
   * @param {Object} fileData - File data object
   * @param {Object} uploadOperations - Upload operations object containing abort controls
   * @returns {Promise<void>}
   */
  uploadToStorage: async (presignedUrl, fileData, uploadOperations) => {
    return await fileUpload.uploadSingleFile(presignedUrl, fileData, uploadOperations);
  },

  /**
   * Create content entry in database with abort signal
   * @param {Object} contentMetadata - Metadata for content creation
   * @returns {Promise<Object>} - The created content object
   */
  createContentEntry: async contentMetadata => {
    const shootId = 0;

    // Note: We need to modify ContentApi to support abort signals
    // For now, this will work but won't be cancellable until API module is updated
    const batchResponse = await ContentApi.addContent({
      id: shootId,
      payload: {
        content: contentMetadata
      }
    });

    // Return the newly added content (first item in the response)
    const contentData = batchResponse.data.content || batchResponse.data.images;
    return contentData[0];
  },

  /**
   * Prepare file data with metadata
   * @param {File} file - The file to process
   * @param {Function} progressCallback
   * @returns {Promise<Object>} - File data object
   */
  prepareFileData: async file => {
    // Get dimensions and other metadata
    const metadata = await fileUpload.getMediaMetadata(file);

    // Calculate aspect ratio with fallback for documents
    let aspectRatio;
    if (metadata.width > 0 && metadata.height > 0) {
      aspectRatio = parseFloat((metadata.width / metadata.height).toFixed(3));
    } else {
      // Default aspect ratio for documents and other files without dimensions
      aspectRatio = 1.0;
    }

    // Return structured file data
    return {
      name: file.name,
      size: file.size,
      relativePath: file.name,
      contentType: file.type,
      isVideo: file.type.startsWith('video/'),
      aspectRatio: aspectRatio,
      duration: file.type.startsWith('video/') ? metadata.duration : 0,
      file: file
    };
  },

  /**
   * Get dimensions and metadata from media file
   * This exactly mirrors the implementation in UploadImagesDialog
   * @param {File} file - The file to process
   * @returns {Promise<Object>} - Object with width, height, and duration
   */
  getMediaMetadata: file => {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);

      if (file.type.startsWith('video/')) {
        // Handle video files
        const video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
          URL.revokeObjectURL(objectUrl);
          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            duration: Math.round(video.duration)
          });
        };

        video.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Failed to load video metadata'));
        };

        video.src = objectUrl;
      } else if (file.type.startsWith('image/')) {
        // Handle image files
        const img = new Image();

        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve({
            width: img.width,
            height: img.height,
            duration: 0
          });
        };

        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Failed to load image'));
        };

        img.src = objectUrl;
      } else {
        // Default for other file types
        URL.revokeObjectURL(objectUrl);
        resolve({
          width: 0,
          height: 0,
          duration: 0
        });
      }
    });
  },

  /**
   * Upload a single file to storage
   * @param {string} presignedUrl - The presigned URL to upload to
   * @param {Object} fileData - The file data object with file and metadata
   * @param {Object} uploadOperations - Upload operations object containing abort controls
   * @returns {Promise} - Resolves when upload completes
   */
  uploadSingleFile: (presignedUrl, fileData, uploadOperations) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Store XMLHttpRequest in upload operations for cancellation
      uploadOperations.xhrRequest = xhr;

      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', fileData.contentType);

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Network error during upload'));
      };

      xhr.onabort = () => {
        reject(new Error('Upload cancelled by user'));
      };

      // Check if already cancelled before starting
      if (uploadOperations.isCancelled) {
        reject(new Error('Upload cancelled by user'));
        return;
      }

      xhr.send(fileData.file);
    });
  },

  /**
   * Generate video thumbnail from video file
   * @param {File} videoFile - The video file to generate thumbnail from
   * @returns {Promise<string>} - Promise that resolves with thumbnail blob URL
   */
  generateVideoThumbnail: videoFile => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.preload = 'metadata';
      video.muted = true; // Required for some browsers

      video.onloadedmetadata = () => {
        // Set canvas size to video dimensions (with max size limit)
        const maxSize = 200; // Max thumbnail size
        const aspectRatio = video.videoWidth / video.videoHeight;

        if (video.videoWidth > video.videoHeight) {
          canvas.width = Math.min(maxSize, video.videoWidth);
          canvas.height = canvas.width / aspectRatio;
        } else {
          canvas.height = Math.min(maxSize, video.videoHeight);
          canvas.width = canvas.height * aspectRatio;
        }

        // Seek to 1 second or 10% of video duration, whichever is smaller
        const seekTime = Math.min(1, video.duration * 0.1);
        video.currentTime = seekTime;
      };

      video.onseeked = () => {
        try {
          // Draw video frame to canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          // Convert canvas to blob
          canvas.toBlob(
            blob => {
              if (blob) {
                const thumbnailUrl = URL.createObjectURL(blob);
                resolve(thumbnailUrl);
              } else {
                reject(new Error('Failed to create thumbnail blob'));
              }
            },
            'image/jpeg',
            0.8
          ); // 80% quality JPEG

          // Clean up
          URL.revokeObjectURL(video.src);
        } catch (error) {
          reject(error);
        }
      };

      video.onerror = () => {
        reject(new Error('Failed to load video for thumbnail generation'));
      };

      // Load the video file
      video.src = URL.createObjectURL(videoFile);
    });
  },

  /**
   * Generate video thumbnail asynchronously (non-blocking)
   * @param {File} videoFile - The video file to generate thumbnail from
   * @returns {Promise<string>} - Promise that resolves with thumbnail blob URL
   */
  generateVideoThumbnailAsync: async videoFile => {
    return fileUpload.generateVideoThumbnail(videoFile);
  },

  /**
   * Get active uploads map for external registration (used by UploadImagesDialog)
   * @returns {Map} - The active uploads map
   */
  getActiveUploadsMap: () => {
    return activeUploads;
  },

  /**
   * Auto-upload a generated video with deduplication
   * @param {string} videoUrl - The video URL to upload
   * @param {Object} metadata - Video metadata (cameraEffect, fileName, etc.)
   * @param {Object} store - Vuex store instance for progress tracking
   * @returns {Promise<Object|null>} - Upload result or null if already uploaded/failed
   */
  autoUploadGeneratedVideo,

  /**
   * Check if a video URL has already been uploaded
   * @param {string} videoUrl - The video URL to check
   * @returns {boolean} - True if upload is in progress or already completed
   */
  isVideoAlreadyUploaded,

  /**
   * Cleanup old auto-upload registry entries
   */
  cleanupAutoUploadRegistry
};

export default fileUpload;
